import { orderSections } from '../helpers/orderSections';
import type {
  CastBlockContent,
  CastParagraph,
  CastRoot,
  CastSection,
  CastTable,
  CastTableCell,
  CastTableRow,
} from '../types';

export type ParagraphAncestor = CastSection | CastTable | CastTableCell | CastTableRow;

export type ParagraphEntry = {
  /** All ancestor containers from outermost (section) to innermost (cell). */
  ancestors: ParagraphAncestor[];
  /** Position in document-order traversal (canonical section order). */
  documentOrder: number;
  paragraph: CastParagraph;
  /** The owning section. */
  section: CastSection;
};

export type ParagraphIndex = {
  byId: Map<string, ParagraphEntry>;
  /** All paragraphs in document order. Use entry.documentOrder to index back. */
  ordered: ParagraphEntry[];
};

/**
 * Walks a CastRoot once to build a paragraph index. Visits sections in the same
 * canonical order the serializer emits (see `orderSections`) so document-order
 * indices line up with how the output will be emitted.
 */
export function indexParagraphs(root: CastRoot): ParagraphIndex {
  const ordered: ParagraphEntry[] = [];
  const byId = new Map<string, ParagraphEntry>();

  function visitSection(section: CastSection): void {
    for (const block of section.children) {
      visitBlock(block, section, [section]);
    }
  }

  function visitBlock(
    block: CastBlockContent,
    section: CastSection,
    ancestors: ParagraphAncestor[]
  ): void {
    if (block.type === 'paragraph') {
      const entry: ParagraphEntry = {
        ancestors,
        documentOrder: ordered.length,
        paragraph: block,
        section,
      };
      ordered.push(entry);
      /**
       * Only the first paragraph wins for a given id — later duplicates are
       * unreachable via byId lookup. Duplicates are unexpected (ids should be
       * unique per source paragraph) but we don't throw, to stay resilient.
       */
      if (!byId.has(block.id)) byId.set(block.id, entry);
      return;
    }
    visitTable(block, section, ancestors);
  }

  function visitTable(
    table: CastTable,
    section: CastSection,
    ancestors: ParagraphAncestor[]
  ): void {
    const tableAncestors = [...ancestors, table];
    for (const row of table.children) {
      const rowAncestors = [...tableAncestors, row];
      visitRow(row, section, rowAncestors);
    }
  }

  function visitRow(row: CastTableRow, section: CastSection, ancestors: ParagraphAncestor[]): void {
    for (const cell of row.children) {
      const cellAncestors = [...ancestors, cell];
      for (const block of cell.children) {
        visitBlock(block, section, cellAncestors);
      }
    }
  }

  for (const section of orderSections(root.children)) visitSection(section);

  return { byId, ordered };
}
